/**
 * "Something's gone wrong": the likely causes in order, in the screens' own
 * words, with a button to the place to check. These never start a task —
 * `replyTo.ts`'s problem-phrase detection keeps a problem statement from
 * being read as a request to do the thing that's going wrong.
 */
import { getSidebarItem } from "../../../config/sidebarConfig";
import { invitationsQuery, noticesQuery } from "../../../services/queries";
import { viewQuery } from "../scopedQuery";
import { mapNoticesToCalendarEventCores } from "../../../utils/noticeCalendarEvents";
import { usersHref } from "../../users/usersHandover";
import { dayLabel } from "../dateRange";
import { loadFeed, noticeLink, pickNotice, viewsOf } from "./notices";
import { AnswerContext, defineEntry, isSlotQuestion } from "./types";
import { quoted } from "./wording";

const calendarPage = getSidebarItem("calendar");
const openCalendar = () => ({ label: `Open ${calendarPage.label}`, href: calendarPage.path });

const noticesPage = getSidebarItem("notices");
const openNotices = () => ({ label: `Open ${noticesPage.label}`, href: noticesPage.path });

const drivePage = getSidebarItem("drive");
const openDrive = () => ({ label: `Open ${drivePage.label}`, href: drivePage.path });

const orgOf = ({ user }: AnswerContext) => user?.schoolId || user?.orgId;

// Grouped by sidebar item, and inserted into the catalogue at each one's own
// place, so the whole catalogue stays in sidebar order.

export const troubleshootingCalendarEntries = [
  // The event was made, but doesn't show on the Calendar
  defineEntry<{ latest: { title: string; date: string } | null }>({
    id: "troubleshooting.eventNotOnCalendar",
    sidebarItem: "calendar",
    alsoNeeds: ["notices"],
    examples: [
      "My event isn't showing on the calendar",
      "The event I made isn't on the calendar",
      "My event isn't showing up on the calendar",
      "Why isn't my event showing up on the calendar?",
      "I can't see my event on the calendar",
      "The event I made isn't appearing on the calendar",
      "My event didn't show up on the calendar",
      "Sports day isn't showing on the calendar",
      "I added an event but it isn't showing on the calendar",
      "Why can't I find my event on the calendar?",
      "The event I created isn't showing",
      "the event is missing",
      "The one I made isn't on the calendar.",
      "why isnt it on the calender",
      "I published it but the calendar is empty",
      "The event is not on the calendar",
    ],
    answer: {
      load: async (_, ctx) => {
        const notices = await ctx.queryClient.fetchQuery(viewQuery(ctx.user, noticesQuery(ctx.user?.schoolId, ctx.token)));
        const events = mapNoticesToCalendarEventCores(notices);
        if (events.length === 0) return { latest: null };
        const [latest] = [...events].sort(
          (a, b) => new Date(b.notice.createdAt).getTime() - new Date(a.notice.createdAt).getTime()
        );
        return { latest: { title: latest.fullTitle, date: dayLabel(latest.start) } };
      },
      templates: [
        ({ latest }) =>
          `A few things stop an Event showing: it was sent as an Information Notice rather than an Event, it's still scheduled rather than sent, or the Calendar's Circle filter is hiding that Circle.${latest ? ` Your last Event, ${quoted(latest.title)}, is on the Calendar for ${latest.date}.` : ""}`,
        ({ latest }) =>
          `Check three things: it was made as an Event (not an Information Notice), it's been sent rather than left scheduled, and the Circle filter on the Calendar isn't hiding its Circle.${latest ? ` For reference, ${quoted(latest.title)} is there, for ${latest.date}.` : ""}`,
        ({ latest }) =>
          `An Event usually isn't showing because it was made as an Information Notice instead, it's still scheduled, or the Calendar's Circle filter is hiding it.${latest ? ` Your most recent Event, ${quoted(latest.title)}, shows there for ${latest.date}.` : ""}`,
      ],
      link: openCalendar,
    },
  }),
];

export const troubleshootingNoticesEntries = [
  // A sent Notice, with how many of its audience have seen it
  defineEntry<{
    views: (Awaited<ReturnType<typeof viewsOf>> & { title: string; notice: Parameters<typeof noticeLink>[0] }) | null;
    general?: boolean;
  }>({
    id: "troubleshooting.parentsCantSeeNotice",
    sidebarItem: "notices",
    examples: [
      "Parents can't see my notice",
      "A parent cannot see a notice",
      "A parent can't see my notice",
      "Parents can't see the notice I sent",
      "Has no one seen my notice?",
      "Why can't parents see my notice?",
      "My notice isn't showing for parents",
      "Parents say they can't find my notice",
      "Parents in that class can't see the notice",
      "My notice isn't appearing for parents",
      "Why hasn't anyone seen my notice?",
      "Families can't see the notice I sent",
      "Why hasn't anybody opened my notice?",
      "parents cant see it",
      "What should I try next?",
    ],
    slots: { notice: "" },
    answer: {
      load: async ({ notice: said = "" }, ctx) => {
        const { sent } = await loadFeed(ctx);
        if (sent.length === 0) return { views: null };
        const pick = pickNotice(sent, said);
        if (isSlotQuestion(pick)) return { views: null, general: true };
        const { notice } = pick;
        return { views: { notice, title: notice.title, ...(await viewsOf(notice, ctx)) } };
      },
      templates: [
        ({ views, general }) =>
          general
            ? "Check that it was published to their Circle and that they use the app."
            : views
              ? `${quoted(views.title)} has been viewed by ${views.seen} of ${views.total}. If the rest haven't seen it, check it went to the right Circle, that it's sent rather than still scheduled, and that those parents have joined the app and are in that Circle.`
              : "You haven't sent any Notices yet, so there's nothing to check views on.",
        ({ views, general }) =>
          general
            ? "Check that it was published to their Circle and that they use the app."
            : views
              ? `So far ${views.seen} of ${views.total} have viewed ${quoted(views.title)}. For the rest: confirm the Circle it went to, that it isn't still scheduled, and that they've joined the app and are in that Circle.`
              : "There are no sent Notices yet to check.",
        ({ views, general }) =>
          general
            ? "Check that it was published to their Circle and that they use the app."
            : views
              ? `${quoted(views.title)}: ${views.seen} of ${views.total} viewed. A parent who hasn't seen it may be in a different Circle, may not have joined the app yet, or the Notice may still be scheduled rather than sent.`
              : "Once you send a Notice, I can check who's seen it.",
      ],
      link: (_, { views }) => (views ? noticeLink(views.notice) : openNotices()),
    },
  }),

  // Can't unsend or edit a sent Notice
  defineEntry({
    id: "troubleshooting.sentToWrongCircle",
    sidebarItem: "notices",
    examples: [
      "I accidentally sent it to the whole school",
      "I accidentally sent a notice to the wrong circle",
      "I accidentally sent it to the wrong circle",
      "I sent it to the wrong circle",
      "I accidentally sent the wrong notice to everyone",
      "I accidentally sent a notice to the wrong group",
      "I accidentally sent that to a different circle than I meant",
      "I accidentally sent a notice to everyone",
      "That notice accidentally went to the wrong people",
      "I accidentally sent it to the whole school by mistake",
      "Oops, I accidentally sent that notice to the wrong class",
      "Can I edit the notice I sent?",
      "Can I edit a notice after sending it?",
      "Can I delete a notice I sent?",
      "Can I unsend a notice?",
      "delete the notice",
      "edit the notice I sent yesterday",
      "I think I sent it to the wrong circle",
    ],
    answer: {
      load: async () => ({}),
      templates: [
        () =>
          "A sent Notice can't be unsent or edited. On Notices, press Duplicate on it, fix who it's for, and send a short correction.",
        () =>
          "There's no way to unsend or edit a Notice once it's out. Duplicate it from Notices, change the audience, and send a correction.",
        () =>
          "Once sent, a Notice is final. Find it on Notices, press Duplicate, correct who it goes to, and send a short follow-up.",
      ],
      link: openNotices,
    },
  }),

  // Parents didn't get a push notification (the dashboard can't tell)
  defineEntry({
    id: "troubleshooting.noPushNotification",
    sidebarItem: "notices",
    examples: [
      "Parents didn't get a push notification",
      "Parents didn't get a notification",
      "Parents aren't getting notified",
      "Parents say they aren't getting notifications",
      "A parent isn't getting push notifications",
      "Families aren't receiving notifications",
      "Why didn't parents get notified?",
      "Parents didn't get a notification about my notice",
      "Why did no one get notified about the notice?",
      "Parents aren't getting notified when I send a notice",
    ],
    answer: {
      load: async () => ({}),
      templates: [
        () =>
          "That's usually the parent's phone, not Noticeboard: ask them to check notifications are switched on for the app in their phone's settings.",
        () =>
          "The dashboard can't tell whether a push notification arrived. Ask the parent to check their phone's notification settings for the app.",
        () =>
          "That's down to their phone settings rather than Noticeboard. Ask them to turn on notifications for the app and check they're not muted.",
      ],
      link: openNotices,
    },
  }),
];

export const troubleshootingUsersEntries = [
  // Invited but not showing as joined
  defineEntry<{ count: number }>({
    id: "troubleshooting.invitedParentMissing",
    sidebarItem: "users",
    examples: [
      "I invited a parent but they aren't appearing",
      "I invited a parent but they aren't showing up",
      "The parent I invited isn't showing on the list",
      "I invited someone but they aren't appearing as a user yet",
      "Why hasn't the parent I invited joined?",
      "I sent an invite but they aren't appearing",
      "The invite I sent isn't showing them as joined",
      "I invited a family but they aren't showing on Users",
      "Why isn't the parent I invited showing up?",
      "I can't see the parent I invited",
    ],
    answer: {
      load: async (_, ctx) => {
        const invitations = await ctx.queryClient.fetchQuery(viewQuery(ctx.user, invitationsQuery(orgOf(ctx), ctx.token)));
        return { count: invitations.length };
      },
      templates: [
        ({ count }) =>
          `Until they join, they're under Users → Pending Invitations${count > 0 ? ` (${count} waiting there now)` : ""}. Try resending the invite, and check the number or email you used is right.`,
        ({ count }) =>
          `They'll sit under Pending Invitations on Users${count > 0 ? `, where ${count} are waiting` : ""} until they accept. Resend it, and double-check the number or email.`,
        ({ count }) =>
          `That's expected until they accept: look under Users → Pending Invitations${count > 0 ? ` (${count} there)` : ""}. Resend the invite, or check the contact details are correct.`,
      ],
      link: () => ({ label: "See Pending Invitations", href: usersHref("invitations") }),
    },
  }),
];

export const troubleshootingDriveEntries = [
  // Uploaded something, can't find it
  defineEntry({
    id: "troubleshooting.uploadNotFound",
    sidebarItem: "drive",
    alsoNeeds: ["notices"],
    examples: [
      "I uploaded something but can't find it",
      "I cannot find my uploaded file",
      "I can't find my uploaded file",
      "I uploaded a file but can't find it",
      "Where did my upload go?",
      "I can't find the file I uploaded",
      "I added a file but now I can't see it",
      "Why has my upload disappeared?",
      "I shared a file but can't find it now",
      "Where is the document I uploaded?",
      "I can't find the file I added to Drive",
      "I uploaded a worksheet but now I can't find it",
      "wheres the file i added",
      "I uploaded one earlier and can't find it",
      "I can't see the file on my notice",
    ],
    answer: {
      load: async () => ({}),
      templates: [
        () =>
          "Uploaded files are in Drive, under the Circle they were shared with. If it was attached to a Notice instead, it lives on that Notice rather than in Drive.",
        () =>
          "Check Drive, under the Circle it was shared with. An attachment added to a Notice stays on that Notice, not in Drive.",
        () =>
          "It should be in Drive, filed under whichever Circle it was shared with. If you attached it to a Notice instead, look there rather than Drive.",
      ],
      link: openDrive,
    },
  }),
];
